import React from 'react';
import { TestFlightTester } from '../../types';

interface TestFlightTestersTableProps {
  testers: TestFlightTester[];
  selectedTesters: string[];
  onSelectionChange: (testerId: string, selected: boolean) => void;
  onSelectAll: (selected: boolean) => void;
  onSort: (column: string, order: 'asc' | 'desc') => void;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  isLoading?: boolean;
}

const TestFlightTestersTable: React.FC<TestFlightTestersTableProps> = ({
  testers,
  selectedTesters,
  onSelectionChange,
  onSelectAll,
  onSort,
  sortBy,
  sortOrder,
  isLoading = false
}) => {
  const allSelected = testers.length > 0 && selectedTesters.length === testers.length;
  const someSelected = selectedTesters.length > 0 && selectedTesters.length < testers.length;

  const handleSelectAllChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onSelectAll(e.target.checked);
  };

  const handleSort = (column: string) => {
    const newOrder = sortBy === column && sortOrder === 'asc' ? 'desc' : 'asc';
    onSort(column, newOrder);
  };

  const getStatusClass = (status: string) => {
    const statusClasses = {
      'INVITED': 'status-pending',
      'INSTALLED': 'status-success',
      'NO_RESPONSE': 'status-warning',
      'BETA_APPROVED': 'status-success',
      'BETA_REJECTED': 'status-error'
    };
    return statusClasses[status as keyof typeof statusClasses] || 'status-default';
  };

  const getSortIndicator = (column: string) => {
    if (sortBy !== column) return '↕️';
    return sortOrder === 'asc' ? '↑' : '↓';
  };

  const getName = (tester: TestFlightTester) => {
    const name = [tester.attributes.firstName, tester.attributes.lastName]
      .filter(Boolean)
      .join(' ');
    return name || '-';
  };

  return (
    <div className="table-container">
      <div className="table-header">
        <div className="table-actions">
          <div className="table-info">
            <span>{testers.length} testers</span>
          </div>
        </div>
      </div>
      
      <div className="table-wrapper">
        <table className="data-table">
          <thead>
            <tr>
              <th style={{ width: '50px' }}>
                <input
                  type="checkbox"
                  checked={allSelected}
                  ref={(input) => {
                    if (input) input.indeterminate = someSelected;
                  }}
                  onChange={handleSelectAllChange}
                />
              </th>
              <th 
                className="sortable" 
                onClick={() => handleSort('email')}
                style={{ cursor: 'pointer' }}
              >
                Email <span className="sort-indicator">{getSortIndicator('email')}</span>
              </th>
              <th 
                className="sortable" 
                onClick={() => handleSort('name')}
                style={{ cursor: 'pointer' }}
              >
                Name <span className="sort-indicator">{getSortIndicator('name')}</span>
              </th>
              <th 
                className="sortable" 
                onClick={() => handleSort('status')}
                style={{ cursor: 'pointer' }}
              >
                Status <span className="sort-indicator">{getSortIndicator('status')}</span>
              </th>
              <th 
                className="sortable" 
                onClick={() => handleSort('group')}
                style={{ cursor: 'pointer' }}
              >
                Group <span className="sort-indicator">{getSortIndicator('group')}</span>
              </th>
              <th 
                className="sortable" 
                onClick={() => handleSort('inviteType')}
                style={{ cursor: 'pointer' }}
              >
                Invite Type <span className="sort-indicator">{getSortIndicator('inviteType')}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={6} className="text-center text-gray-500">
                  <div className="loading-spinner">
                    <div className="spinner"></div>
                    <p>Loading testers...</p>
                  </div>
                </td>
              </tr>
            ) : testers.length === 0 ? (
              <tr>
                <td colSpan={6} className="text-center text-gray-500">
                  No testers found
                </td>
              </tr>
            ) : (
              testers.map(tester => (
                <tr 
                  key={tester.id} 
                  className={selectedTesters.includes(tester.id) ? 'selected' : ''}
                >
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedTesters.includes(tester.id)}
                      onChange={(e) => onSelectionChange(tester.id, e.target.checked)}
                      className="tester-checkbox"
                    />
                  </td>
                  <td>{tester.attributes.email}</td>
                  <td>{getName(tester)}</td>
                  <td>
                    <span className={`status-badge ${getStatusClass(tester.attributes.betaTesterState || tester.attributes.state || 'UNKNOWN')}`}>
                      {tester.attributes.betaTesterState || tester.attributes.state || 'UNKNOWN'}
                    </span>
                  </td>
                  <td>{tester.groupName}</td>
                  <td>{tester.attributes.inviteType || '-'}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default TestFlightTestersTable;
